import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

// Common Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

// Hooks
import { useListParams } from "hooks/useListParams"
import {
  useUnits,
  useDeleteUnit,
} from "hooks/useUnit"

// Utils
import { formatReadableDate } from "utils/formatDate"

// Local Components
import UnitAddEdit from "./UnitAddEdit"
import UnitHeader from "./UnitHeader"

const UnitList = () => {
  document.title = "Accessories Unit List"

  // -------------------- STATE --------------------
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // -------------------- LIST PARAMS --------------------
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  // -------------------- API --------------------
  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useUnits(queryParams)
  

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteUnit()

  // -------------------- MODAL HANDLERS --------------------
  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Unit deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete unit.")
    }
  }

  // -------------------- TABLE COLUMNS --------------------
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.original.id || "-"}
          </div>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {row.original.name || "-"}
          </div>
        ),
      },
      {
        header: "Description",
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {row.original.description || "-"}
          </div>
        ),
      },
      {
        header: "Created At",
        cell: ({ row }) => (
          <div style={{ whiteSpace: "nowrap" }}>
            {row.original.created_at
              ? formatReadableDate(row.original.created_at)
              : "-"}
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex flex-wrap gap-1">
              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(data)}
                id={`edit-${data.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${data.id}`}>
                Edit
              </UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(data)}
                id={`delete-${data.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${data.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  // -------------------- RENDER --------------------
  return (
    <PageWrapper title="Accessories Unit List" breadcrumbItem="Accessories Unit List">
      <UnitHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      <UnitAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default UnitList
